import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Plus,
  ImageIcon,
  Check,
  AlertCircle,
  Pencil,
  Search,
  ChevronDown,
  Sparkles,
  Loader2,
  X,
  Video,
  Lock,
  Trash2,
} from "lucide-react";

import { supabase } from "../lib/supabase";
import { retrySupabase } from "../utils/retry";
import { fetchStripeBillingPortalUrl } from "../lib/paymentServer";
import { categorizeProduct } from "../lib/categorizeProduct";
import VariantForm, { ProductImage, Variant } from "../components/VariantForm";
import CachedImg from "../components/CachedImg";
import { useHomeBanners } from "../hooks/useHomeBanners";
import { HomeBannersView } from "../components/HomeBanners";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { isHeicFile, isWebpFile, isUnsupportedUploadFile, UnsupportedImageFormatError, convertHeicToJpeg } from "../utils/heicConvert";
import { resizeImageIfNeeded } from "../utils/imageResize";
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
} from "../utils/crearAnuncioStorage";
import type { CrearAnuncioModo } from "../utils/crearAnuncioStorage";
import {
  getCachedImageByKey,
  releaseCachedByKey,
  getCachedSignedUrl,
  setCachedSignedUrl,
} from "../utils/imageCache";
import { trackInteraction } from "../utils/trackInteraction";
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { getCached, setCached, CACHE_KEYS } from "../utils/sessionCache";
import { gestionaPlanEnPortalStripe } from "../lib/gestionPlan";
import AddToHomescreenPrompt from "../components/AddToHomescreenPrompt";
import { buildThumbnailBlob } from "../utils/thumbnail";

const FASHION_LABEL = "Moda e Indumentaria";
const AR_TZ = "America/Argentina/Buenos_Aires";

/**
 * La carga inicial no llegó al server (red caída, 5xx, sesión que no se pudo
 * validar). Se distingue del "no tenés empresa" porque el usuario NO tiene que
 * hacer nada distinto: reintentar alcanza. Ver
 * ADR-2026-08-02-no-company-associated-diagnostico.
 */
class ErrorDeConexion extends Error {
  constructor() {
    super("No pudimos cargar tus productos. Revisá tu conexión y reintentá.");
    this.name = "ErrorDeConexion";
  }
}

const getGreeting = () => {
  try {
    const hourStr = new Intl.DateTimeFormat("es-AR", {
      hour: "numeric",
      hour12: false,
      timeZone: AR_TZ,
    }).format(new Date());
    const hour = parseInt(hourStr, 10);
    if (hour >= 6 && hour < 15) return "Buenos días";
    if (hour >= 15 && hour < 20) return "Buenas tardes";
    return "Buenas noches";
  } catch (_e) {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 15) return "Buenos días";
    if (hour >= 15 && hour < 20) return "Buenas tardes";
    return "Buenas noches";
  }
};

export const SeleccionarProductosAnuncio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileHideBottomNav } = useSidebar();
  const modo = useMemo<CrearAnuncioModo>(() => {
    const m = new URLSearchParams(location.search).get("modo");
    return m === "personalizado" ? "personalizado" : "rapido";
  }, [location.search]);

  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<string[]>(() => {
    // Prefer URL ids (e.g. coming back from PruebaNuevaInterfaz after removing a product)
    const urlIds = new URLSearchParams(location.search).get("ids");
    if (urlIds) {
      const parsed = urlIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parsed.length > 0) return parsed;
    }
    const freshState = loadLastCrearAnuncioState();
    const arr = freshState?.selectedVariantIds ?? [];
    return Array.isArray(arr) ? arr.map((n: any) => String(n)) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** El error de arriba se arregla reintentando (no es "no tenés empresa"). */
  const [esErrorReintentable, setEsErrorReintentable] = useState(false);
  const [signedUrls, setSignedUrls] = useState<
    Record<string, { full: string; thumbnail: string }>
  >({});

  // Add product modal state
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState<{
    nombre: string;
    images: ProductImage[];
  }>({ nombre: "", images: [] });

  // Edit product modal state (mirror SocialMedia behavior)
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});

  // Search term
  const [query, setQuery] = useState("");

  // Company/cache + lazy-loading helpers
  const [companyId, setCompanyId] = useState<string | null>(null);
  const companyIdRef = useRef<string | null>(null);
  const [, setIsFashion] = useState<boolean | null>(() => {
    const last = loadLastCrearAnuncioState();
    if (!last?.rubro) return null;
    return last.rubro === FASHION_LABEL;
  });
  const [userRubro, setUserRubro] = useState<string>(() => {
    const last = loadLastCrearAnuncioState();
    return (last?.rubro as string) ?? FASHION_LABEL;
  });
  const [shortName, setShortName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [isFreePlan, setIsFreePlan] = useState<boolean>(false);

  // Brand switcher
  type BrandRow = {
    id: string;
    name: string;
    is_default: boolean;
    logo_url: string | null;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [, setCompanyLogoUrl] = useState<string | null>(null);
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [switchingBrand, setSwitchingBrand] = useState(false);
  const brandSwitcherRef = useRef<HTMLDivElement>(null);

  // En mobile, al seleccionar productos aparece la barra de acción fija (CTA
  // de generar) abajo: en ese "modo tarea" ocultamos la barra de navegación
  // inferior para que no se apilen. En el estado raíz (sin selección) la barra
  // de navegación se muestra normalmente.
  useEffect(() => {
    setMobileHideBottomNav(selectedVariants.length > 0);
    return () => setMobileHideBottomNav(false);
  }, [selectedVariants.length, setMobileHideBottomNav]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        brandSwitcherRef.current &&
        !brandSwitcherRef.current.contains(e.target as Node)
      ) {
        setIsBrandDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cargar marcas y marca activa cuando hay companyId
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        // Brands del company
        const { data: brandsData } = await supabase
          .from("brand_profiles")
          .select("id, name, is_default, logo_url")
          .eq("id_company", companyId)
          .eq("is_draft", false)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: true });

        // current_brand_id + logo de la empresa
        const { data: profileData } = await supabase
          .from("profiles")
          .select("current_brand_id")
          .eq("id", user.id)
          .single();

        const { data: companyData } = await supabase
          .from("company_profiles")
          .select("logo_url")
          .eq("id_company", companyId)
          .single();

        if (cancelled) return;

        const list = (brandsData as BrandRow[] | null) ?? [];
        setBrands(list);
        setCompanyLogoUrl((companyData as any)?.logo_url ?? null);

        const fromProfile = (profileData as any)?.current_brand_id as
          | string
          | null;
        const fallback =
          list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
        const resolvedBrandId = fromProfile ?? fallback;
        setCurrentBrandId(resolvedBrandId);

        // Refetch productos filtrado por la marca recién resuelta
        // (la primera carga en fetchVariants pudo no haber tenido brand aún).
        if (resolvedBrandId && companyIdRef.current) {
          fetchProducts(companyIdRef.current, resolvedBrandId).catch(() => {});
        }
      } catch (e) {
        // Silencioso: el pildora solo no se muestra si el fetch falla
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Escuchar cambios de marca disparados desde el sidebar (u otras pantallas)
  useEffect(() => {
    const handler = (e: Event) => {
      const brandId = (e as CustomEvent).detail?.brandId as string | undefined;
      if (!brandId) return;
      setCurrentBrandId(brandId);
      const idCompany = companyIdRef.current;
      if (idCompany) {
        setLoading(true);
        fetchProducts(idCompany, brandId)
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    };
    window.addEventListener("atlas:brand-changed", handler);
    return () => window.removeEventListener("atlas:brand-changed", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (brandId === currentBrandId || switchingBrand) {
        setIsBrandDropdownOpen(false);
        return;
      }
      setSwitchingBrand(true);
      const prev = currentBrandId;
      setCurrentBrandId(brandId); // optimistic
      setIsBrandDropdownOpen(false);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user");
        const { error } = await supabase
          .from("profiles")
          .update({ current_brand_id: brandId })
          .eq("id", user.id);
        if (error) throw error;

        // Refetch productos con el filtro de la nueva marca
        const idCompany = companyIdRef.current;
        if (idCompany) {
          setLoading(true);
          try {
            await fetchProducts(idCompany, brandId);
          } finally {
            setLoading(false);
          }
        }

        // Avisar al sidebar y al resto de la app que la marca cambió
        window.dispatchEvent(
          new CustomEvent("atlas:brand-changed", { detail: { brandId } }),
        );
      } catch (e) {
        // Rollback si falló el update
        setCurrentBrandId(prev);
      } finally {
        setSwitchingBrand(false);
      }
    },
    [currentBrandId, switchingBrand]
  );

  const activeBrand =
    brands.find((b) => b.id === currentBrandId) ??
    brands.find((b) => b.is_default) ??
    brands[0] ??
    null;

  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingImageKeysRef = useRef<Set<string>>(new Set());
  const cachedKeysRef = useRef<Set<string>>(new Set());

  // Banners de home (prueba gratis / describir la marca): su condición y diseño
  // viven en `useHomeBanners` + `HomeBannersView` (compartidos con Interfaz V2).
  // El de marca se suprime al volver de describir la marca (`hideBrandBanner`).
  const homeBanners = useHomeBanners({
    suppressBrand:
      (location.state as { hideBrandBanner?: boolean } | null)?.hideBrandBanner ===
      true,
  });
  // El banner del cuestionario (encuesta de feedback, +10 imágenes) ya no vive
  // acá: pasó a ser un `kind` de `useHomeBanners` / `HomeBannersView` para que
  // lo compartan el clásico y la Interfaz V2, y para que no pueda salir junto
  // con otro banner. Ver ADR-681.

  // Repeat previous generation (session-based)
  const [, setHasGeneratedThisSession] =
    useState<boolean>(() => {
      try {
        return (
          window.sessionStorage.getItem("atlas:crear-anuncio:has-generated") ===
          "1"
        );
      } catch {
        return false;
      }
    });
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] =
    useState(false);
  const [
    showVideoMultipleProductsWarning,
    setShowVideoMultipleProductsWarning,
  ] = useState(false);
  const [userPlan, setUserPlan] = useState<string>("gratis");
  // Cliente de Stripe con suscripción activa → "mejorar plan" va a su Billing
  // Portal, no a nuestro catálogo (ver lib/gestionPlan.ts y ADR-661).
  const [enPortalStripe, setEnPortalStripe] = useState<boolean>(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const [randomMessage, setRandomMessage] = useState<string | null>(null);
  const [showContactQR, setShowContactQR] = useState<boolean>(false);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch random message for greeting (cached — array fetched once, random pick each mount)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cached = getCached<string[]>(CACHE_KEYS.MENSAJES_INICIO);
        if (cached && cached.length > 0) {
          if (mounted)
            setRandomMessage(cached[Math.floor(Math.random() * cached.length)]);
          return;
        }
        const { data: mensajes, error } = await supabase
          .from("mensajes_inicio")
          .select("mensaje");
        if (!error && mensajes && mensajes.length > 0 && mounted) {
          const arr = mensajes.map((m: any) => m.mensaje as string);
          setCached(CACHE_KEYS.MENSAJES_INICIO, arr, 30 * 60 * 1000); // 30 min TTL
          setRandomMessage(arr[Math.floor(Math.random() * arr.length)]);
        }
      } catch (e) {
        console.error("Error loading random message:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      cachedKeysRef.current.forEach((k) => releaseCachedByKey(k));
      cachedKeysRef.current.clear();
    };
  }, []);

  // Fetch company metadata (plan, rubro, analysis, upsell) — runs in parallel with products
  const fetchMetadata = async (idCompany: string) => {
    try {
      // Try cached company stable data first
      type CompanyCache = {
        plan: string;
        rubro: string;
        nombre: string;
        pais: string;
        /** Medio de pago real: decide portal de Stripe vs catálogo (lib/gestionPlan.ts). */
        plataformaPago: string | null;
      };
      let companyCache = getCached<CompanyCache>(CACHE_KEYS.COMPANY_DATA);
      // Una entrada vieja (cacheada antes de que existiera el campo) no sabe el
      // medio de pago → se refetchea en vez de asumir. Sin esto, un cliente de
      // Stripe con caché previa iría al catálogo en vez de a su portal.
      if (companyCache && !("plataformaPago" in companyCache)) companyCache = null;

      if (companyCache) {
        // Use cached stable fields immediately
        setCompanyName(companyCache.nombre);
        setUserPlan(companyCache.plan);
        setEnPortalStripe(
          gestionaPlanEnPortalStripe(companyCache.plataformaPago, companyCache.plan),
        );
        setIsFreePlan(!companyCache.plan || companyCache.plan === "gratis");
        const rubroValue = companyCache.rubro || FASHION_LABEL;
        setIsFashion(rubroValue === FASHION_LABEL);
        setUserRubro(rubroValue);
        mergeLastCrearAnuncioState({ rubro: rubroValue });
      } else {
        // No cache — full fetch
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan, rubro, nombre, pais, plataforma_pago")
          .eq("id_company", idCompany)
          .single();

        const plan = (company as any)?.plan || "gratis";
        const rubro = (company as any)?.rubro || FASHION_LABEL;
        const nombre = (company as any)?.nombre || "";
        const pais = (company as any)?.pais || "Argentina";
        const plataformaPago = ((company as any)?.plataforma_pago ?? null) as string | null;

        // Cache stable fields
        setCached(CACHE_KEYS.COMPANY_DATA, {
          plan,
          rubro,
          nombre,
          pais,
          plataformaPago,
        });

        setCompanyName(nombre);
        setUserPlan(plan);
        setEnPortalStripe(gestionaPlanEnPortalStripe(plataformaPago, plan));
        setIsFreePlan(!plan || plan === "gratis");
        setIsFashion(rubro === FASHION_LABEL);
        setUserRubro(rubro);
        mergeLastCrearAnuncioState({ rubro });

        companyCache = { plan, rubro, nombre, pais, plataformaPago };
      }

      // TODOS los banners de home (upsell, marca, cuestionario, Tienda Nube) los
      // maneja `useHomeBanners` / `HomeBannersView`, compartidos con Interfaz V2.
      // Acá ya no queda ninguno.
    } catch {
      /* noop — los datos de company que sí usa esta pantalla ya se setearon */
    }
  };

  // Fetch products and their images
  const fetchProducts = async (
    idCompany: string,
    brandIdFilter: string | null = null,
  ) => {
    // Si hay brand activa, filtramos por productos.brand_id via embed inner-join.
    // Siempre incluimos además los productos sin marca (`brand_id IS NULL`): son
    // productos de la empresa que nunca se asignaron a una marca y no deben
    // quedar ocultos. Ver [[productos-huerfanos-brand-null]].
    let queryBuilder = supabase
      .from("variantes")
      .select(
        brandIdFilter
          ? "id, nombre, created_at, productos!inner(brand_id)"
          : "id, nombre, created_at",
      )
      .eq("activo", true)
      .eq("id_company", idCompany);

    if (brandIdFilter) {
      queryBuilder = queryBuilder.or(
        `brand_id.eq.${brandIdFilter},brand_id.is.null`,
        { referencedTable: "productos" },
      );
    }

    const { data: variantsData, error: variantsError } = await retrySupabase(
      async () => await queryBuilder.order("created_at", { ascending: false }),
    );

    // Igual que con el perfil: si la lectura no llegó, es un problema de
    // conexión y se arregla reintentando — no hay nada que el usuario deba
    // corregir. Ver ADR-2026-08-02-no-company-associated-diagnostico.
    if (variantsError) throw new ErrorDeConexion();

    let processed: Variant[] = [];
    if (variantsData && variantsData.length > 0) {
      const variantIds = variantsData.map((v) => v.id);

      // Fetch en lotes: con catálogos grandes la lista de variante_id en la URL
      // superaba el límite del gateway REST de Supabase y devolvía HTTP 400,
      // rompiendo toda la carga de productos. fetchInChunks mantiene cada
      // request por debajo del límite. Ver utils/supabaseChunkedIn.
      const imagesData = await fetchInChunks<any>(variantIds, (chunk) =>
        supabase
          .from("productos_imagenes")
          .select("id, url, principal, orden, variante_id")
          .eq("id_company", idCompany)
          .in("variante_id", chunk),
      );

      const imagesByVariantId: Record<string, ProductImage[]> = {};
      imagesData?.forEach((image: any) => {
        const variantId = image.variante_id as string;
        const entry: ProductImage = {
          id: image.id,
          url: image.url,
          principal: image.principal,
          orden: image.orden,
          producto_variante_id: 0,
          variante_id: variantId,
        };
        if (!imagesByVariantId[variantId]) imagesByVariantId[variantId] = [];
        imagesByVariantId[variantId].push(entry);
      });

      processed = variantsData.map((v) => ({
        id: v.id as string,
        nombre: v.nombre,
        images: imagesByVariantId[v.id as string] || [],
      }));
    }

    setVariants(processed);
    setSelectedVariants((prev) => {
      if (!processed.length) {
        if (prev.length === 0) return prev;
        mergeLastCrearAnuncioState({ selectedVariantIds: [] });
        return [];
      }
      const available = new Set(processed.map((v) => v.id));
      const filtered = prev.filter((id) => available.has(id));
      if (filtered.length === prev.length) return prev;
      mergeLastCrearAnuncioState({ selectedVariantIds: filtered });
      return filtered;
    });
  };

  const fetchVariants = async () => {
    try {
      setLoading(true);
      setError(null);
      setEsErrorReintentable(false);

      // Try cached profile first
      type ProfileCache = { id_company: string; name_short: string };
      let profileData = getCached<ProfileCache>(CACHE_KEYS.USER_PROFILE);

      if (!profileData) {
        // Un fallo de RED al leer la sesión o el perfil NO es "este usuario no
        // tiene empresa": antes los dos casos caían en el mismo throw y el
        // usuario veía "No company associated with user" por un blip de
        // conexión, sin más salida que recargar a mano.
        // Ver ADR-2026-08-02-no-company-associated-diagnostico.
        const { data: userData, error: userError } =
          await supabase.auth.getUser();
        const user = userData?.user;
        if (userError) throw new ErrorDeConexion();
        if (!user) throw new Error("No user found");
        const { data: profile, error: profileError } = await retrySupabase(
          async () =>
            await supabase
              .from("profiles")
              .select("id_company, name_short")
              .eq("id", user.id)
              .single(),
        );
        // `PGRST116` (0 filas) es lo único que significa de verdad "no hay
        // perfil"; cualquier otro error es la lectura que no llegó.
        if (profileError && (profileError as any).code !== "PGRST116") {
          throw new ErrorDeConexion();
        }
        const idComp = (profile as any)?.id_company as string | null;
        if (!idComp) throw new Error("No company associated with user");
        profileData = {
          id_company: idComp,
          name_short: (profile as any).name_short || "",
        };
        setCached(CACHE_KEYS.USER_PROFILE, profileData);
      }

      const idCompany = profileData.id_company;
      setCompanyId(idCompany);
      companyIdRef.current = idCompany;
      setShortName(profileData.name_short);

      // Fire metadata fetch in background (don't block product rendering)
      fetchMetadata(idCompany);

      // Fetch products — loading ends as soon as products are ready
      // Para companies con multi-marca: resolver brand ANTES del fetch evita
      // que aparezca un flash de productos de otras marcas al recargar.
      // Para companies sin brand_profile (legacy single-marca): cargar sin
      // filtro como siempre.
      let effectiveBrandFilter = currentBrandId;
      if (!effectiveBrandFilter) {
        const { data: brandRow } = await supabase
          .from("brand_profiles")
          .select("id")
          .eq("id_company", idCompany)
          .eq("is_draft", false)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        if ((brandRow as any)?.id) {
          effectiveBrandFilter = (brandRow as any).id as string;
        }
        // Si no hay brand_profiles (legacy), effectiveBrandFilter queda null
        // y fetchProducts trae todas las variantes del company (igual que antes).
      }
      await fetchProducts(idCompany, effectiveBrandFilter);
      // Lazy-load signed URLs as cards enter viewport (keep existing cache)
    } catch (err) {
      console.error("Error fetching variants:", err);
      setError(
        err instanceof Error ? err.message : "Error al cargar productos",
      );
      setEsErrorReintentable(err instanceof ErrorDeConexion);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoGenerationClick = () => {
    // Check if more than 1 product is selected
    if (selectedVariants.length > 1) {
      setShowVideoMultipleProductsWarning(true);
      return;
    }
    navigate(
      `/redes-sociales/crear-anuncio/disenar-video?ids=${selectedVariants.join(",")}`,
    );
  };

  const handleImageGenerationClick = () => {
    if (selectedVariants.length > 3) {
      setShowMaxImageProductsWarning(true);
      return;
    }
    navigate(
      `/redes-sociales/crear-anuncio/prueba-nueva-interfaz?ids=${selectedVariants.join(",")}`,
    );
  };

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");
      window.location.href = await fetchStripeBillingPortalUrl(user.id);
    } catch (e: any) {
      alert(e?.message || "Error al abrir el portal de facturación");
      setOpeningBillingPortal(false);
    }
  };

  // Desktop = breakpoint sm (≥640px), el mismo que usan las barras inferiores.
  // Toda la funcionalidad de borrado en cantidad y la selección ilimitada
  // están gateadas por esto, así mobile no cambia.
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia("(min-width: 640px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Borrado en cantidad (solo desktop).
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    done: 0,
    total: 0,
  });
  const [showMaxImageProductsWarning, setShowMaxImageProductsWarning] =
    useState(false);

  const handleVariantSelect = useCallback(
    (variantId: string) => {
      // Calculamos `next` a partir del estado actual (no dentro del updater de
      // setState) para no disparar side effects (navigate/merge) durante el
      // render. navigate() hace setState del Router, y hacerlo dentro del
      // updater dispara el warning "Cannot update a component while rendering".
      const prev = selectedVariants;
      let next: string[];
      if (prev.includes(variantId)) {
        next = prev.filter((id) => id !== variantId);
      } else if (isDesktop) {
        // Desktop: selección ilimitada (para poder borrar en cantidad)
        next = [...prev, variantId];
      } else if (prev.length < 3) {
        next = [...prev, variantId];
      } else {
        next = [...prev.slice(1), variantId];
      }
      setSelectedVariants(next);
      mergeLastCrearAnuncioState({ selectedVariantIds: next });
      // Sync URL: si quedan ids los guardamos, si no, limpiamos el param
      const qp = new URLSearchParams(location.search);
      if (next.length > 0) {
        qp.set("ids", next.join(","));
      } else {
        qp.delete("ids");
      }
      const qs = qp.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    },
    [selectedVariants, location.search, location.pathname, navigate, isDesktop],
  );

  // Borra todos los productos seleccionados (desktop). Concurrencia 3, con
  // progreso y manejo de fallos parciales.
  const handleBulkDelete = async () => {
    if (bulkDeleting) return;
    const ids = [...selectedVariants];
    if (ids.length === 0) return;
    try {
      setBulkDeleting(true);
      setError(null);
      setBulkDeleteProgress({ done: 0, total: ids.length });

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");
      const resolvedCompanyId = (profile as any).id_company as string;

      const queue = [...ids];
      let done = 0;
      const failed: string[] = [];
      const deleted: string[] = [];

      const worker = async () => {
        while (queue.length > 0) {
          const variantId = queue.shift();
          if (!variantId) return;
          try {
            await deleteVariantCascade(variantId, resolvedCompanyId);
            deleted.push(variantId);
          } catch (e) {
            console.error("Error deleting variant (bulk):", variantId, e);
            failed.push(variantId);
          }
          done += 1;
          setBulkDeleteProgress({ done, total: ids.length });
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(3, ids.length) }, () => worker()),
      );

      // Sacar de la lista los borrados con éxito
      const deletedSet = new Set(deleted);
      setVariants((prev) => prev.filter((v) => !deletedSet.has(v.id)));

      // Dejar seleccionados solo los que fallaron, para poder reintentar.
      // Si no falló ninguno, la selección queda vacía.
      setSelectedVariants(failed);
      mergeLastCrearAnuncioState({ selectedVariantIds: failed });
      {
        const qp = new URLSearchParams(location.search);
        if (failed.length > 0) {
          qp.set("ids", failed.join(","));
        } else {
          qp.delete("ids");
        }
        const qs = qp.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, {
          replace: true,
        });
      }

      setShowBulkDeleteConfirm(false);

      if (failed.length > 0) {
        setError(
          `Se eliminaron ${deleted.length} producto(s). ${failed.length} fallaron — intentá de nuevo.`,
        );
      }
    } catch (err) {
      console.error("Error en borrado en cantidad:", err);
      setError(
        err instanceof Error ? err.message : "Error al eliminar los productos",
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  // Edit icon: open VariantForm with the selected variant (like SocialMedia)
  const handleEditIconClick = (e: React.MouseEvent, variantId: string) => {
    e.stopPropagation();
    const variant = variants.find((v) => v.id === variantId) || null;
    if (variant) setEditingVariant(variant);
  };

  const handleSaveProduct = async () => {
    const isEditing = !!editingVariant;
    const productName = isEditing
      ? editingVariant?.nombre || ""
      : newProduct.nombre;
    if (isEditing && !productName) return;
    if (!isEditing && newProduct.images.length === 0) return;

    try {
      setSavingProduct(true);
      setImageError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");
      const idCompany = (profile as any).id_company as string;

      if (isEditing && editingVariant) {
        // Update variant name
        const { error: updError } = await supabase
          .from("variantes")
          .update({ nombre: productName })
          .eq("id", editingVariant.id)
          .eq("id_company", idCompany);
        if (updError) throw updError;

        // Categorización IA tras editar la variante (best-effort)
        try {
          const { data: varRow } = await supabase
            .from("variantes")
            .select("product_id")
            .eq("id", editingVariant.id)
            .single();
          const editProductId = (varRow as any)?.product_id;
          if (editProductId) {
            categorizeProduct(String(editProductId), {
              modo: "editar",
              userId: user.id,
            });
          }
        } catch {}

        // Close modal and refresh list to reflect changes
        setEditingVariant(null);
        await fetchVariants();
      } else {
        // If name was left empty, assign a placeholder "Producto N"
        // based on the current count of products for the company.
        // The backend will replace this with the AI-generated name asynchronously.
        let effectiveName = newProduct.nombre.trim();
        if (!effectiveName) {
          const { count } = await supabase
            .from("productos")
            .select("id", { count: "exact", head: true })
            .eq("id_company", idCompany);
          effectiveName = `Producto ${(count ?? 0) + 1}`;
        }

        // 1) Create product (asociado a la marca activa si hay una)
        const productoPayload: Record<string, any> = {
          id_company: idCompany,
          nombre: effectiveName,
          tiene_variantes: false,
        };
        if (currentBrandId) {
          productoPayload.brand_id = currentBrandId;
        }
        const { data: producto, error: productoError } = await supabase
          .from("productos")
          .insert(productoPayload)
          .select("id")
          .single();
        if (productoError) throw productoError;

        const productId = String(producto.id);

        // 2) Create variant for that product
        const { data: variant, error: variantError } = await supabase
          .from("variantes")
          .insert({
            id_company: idCompany,
            nombre: effectiveName,
            activo: true,
            product_id: productId,
          })
          .select("id")
          .single();
        if (variantError) throw variantError;

        const variantId = variant.id as string;

        // 3) Upload images (full + opt) and insert DB rows storing only filename
        for (let i = 0; i < newProduct.images.length; i++) {
          const imgData = newProduct.images[i];
          if (!imgData.tempFile) continue;

          // Validate file size (limit: 100 MB)
          const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
          if (imgData.tempFile.size > MAX_FILE_SIZE) {
            throw new Error(
              "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
            );
          }

          // Unique filename - sanitize to remove spaces and special characters
          const timestamp = Date.now();
          const originalName = imgData.fileName || imgData.tempFile.name;
          const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_");
          const fileBaseName = `${timestamp}_${sanitizedName}`;

          // Upload full
          const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
          const { error: fullErr } = await supabase.storage
            .from("productos")
            .upload(fullPath, imgData.tempFile);
          if (fullErr) throw fullErr;

          // Create optimized thumbnail using canvas
          try {
            const blob = await buildThumbnailBlob(imgData.tempFile as File);
            if (!blob) throw new Error("no se pudo generar el thumbnail");

            const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
            const { error: optErr } = await supabase.storage
              .from("productos")
              .upload(optPath, blob);
            if (optErr)
              console.warn(
                "Optimized upload failed, continuing with full only",
                optErr,
              );
          } catch (optErr) {
            console.warn("Error creating optimized image, continuing", optErr);
          }

          // Insert DB row with filename only
          const { error: imageError } = await supabase
            .from("productos_imagenes")
            .insert({
              id_company: idCompany,
              variante_id: variantId,
              url: fileBaseName,
              principal: imgData.principal || i === 0,
              orden: imgData.orden || i + 1,
            });
          if (imageError) throw imageError;
        }

        // Categorización IA tras crear la variante y subir imágenes
        categorizeProduct(String(productId), {
          modo: "crear",
          completarNombre: !newProduct.nombre.trim(),
          userId: user.id,
        });

        // Reset and refresh immediately — backend handles AI naming async
        setShowAddForm(false);
        setNewProduct({ nombre: "", images: [] });
        await fetchVariants();
      }
    } catch (err) {
      console.error("Error saving product:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al guardar producto",
      );
    } finally {
      setSavingProduct(false);
    }
  };

  // Handlers for editing flow (mirroring SocialMedia)
  const handleSetPrincipalImage = async (
    variantId: string,
    imageId: number,
  ) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");
      const idCompany = (profile as any).id_company as string;

      await supabase
        .from("productos_imagenes")
        .update({ principal: false })
        .eq("variante_id", variantId)
        .eq("id_company", idCompany);

      await supabase
        .from("productos_imagenes")
        .update({ principal: true })
        .eq("id", imageId)
        .eq("id_company", idCompany);

      const { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url, principal, orden, variante_id")
        .eq("variante_id", variantId)
        .eq("id_company", idCompany)
        .order("orden", { ascending: true });

      const updatedImages: ProductImage[] = (imagesData || []).map(
        (img: any) => ({
          id: img.id,
          url: img.url,
          principal: img.principal,
          orden: img.orden,
          producto_variante_id: 0,
          variante_id: img.variante_id,
        }),
      );

      setEditingVariant((prev) =>
        prev && prev.id === variantId
          ? { ...prev, images: updatedImages }
          : prev,
      );
      setVariants((prev) =>
        prev.map((v) =>
          v.id === variantId ? { ...v, images: updatedImages } : v,
        ),
      );
    } catch (err) {
      console.error("Error setting principal image:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Error al actualizar imagen principal",
      );
    }
  };

  const handleImageUpload = async (variantId: string, file: File) => {
    try {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: true }));
      setImageError(null);

      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        setImageError(
          "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
        );
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");

      // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590): el catch del
      // handler muestra el mensaje del error.
      if (isUnsupportedUploadFile(file)) {
        throw new UnsupportedImageFormatError();
      }

      // Convert HEIC if needed
      let processedFile: File = file;
      if (isHeicFile(file) || isWebpFile(file)) {
        try {
          const converted = await convertHeicToJpeg(file, 0.8);
          processedFile = converted.file;
        } catch (e) {
          throw new Error(
            "No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg",
          );
        }
      }

      // Resize image if too large (max 2000px)
      processedFile = await resizeImageIfNeeded(processedFile, 2000, 0.85);

      // Sanitize filename to remove spaces and special characters
      const timestamp = Date.now();
      const originalName = processedFile.name;
      const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileBaseName = `${timestamp}_${sanitizedName}`;

      // Upload full
      const idCompany = (profile as any).id_company as string;
      const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
      const { error: fullErr } = await supabase.storage
        .from("productos")
        .upload(fullPath, processedFile);
      if (fullErr) throw fullErr;

      // Create and upload optimized
      try {
        const blob = await buildThumbnailBlob(processedFile);
        if (!blob) throw new Error("no se pudo generar el thumbnail");

        const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
        const { error: optErr } = await supabase.storage
          .from("productos")
          .upload(optPath, blob);
        if (optErr)
          console.warn(
            "Optimized upload failed, continuing with full only",
            optErr,
          );
      } catch (optErr) {
        console.warn("Error creating optimized image, continuing", optErr);
      }

      // Insert DB row
      const makePrincipal =
        editingVariant?.id === variantId &&
        !(editingVariant.images || []).some((img) => img.principal);
      const { error: imageError } = await supabase
        .from("productos_imagenes")
        .insert({
          id_company: idCompany,
          variante_id: variantId,
          url: fileBaseName,
          principal: !!makePrincipal,
          orden: (editingVariant?.images?.length || 0) + 1,
        });
      if (imageError) throw imageError;

      // Refresh images
      const { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url, principal, orden, variante_id")
        .eq("variante_id", variantId)
        .eq("id_company", idCompany)
        .order("orden", { ascending: true });

      const updatedImages: ProductImage[] = (imagesData || []).map(
        (img: any) => ({
          id: img.id,
          url: img.url,
          principal: img.principal,
          orden: img.orden,
          producto_variante_id: 0,
          variante_id: img.variante_id,
        }),
      );

      setEditingVariant((prev) =>
        prev && prev.id === variantId
          ? { ...prev, images: updatedImages }
          : prev,
      );
      setVariants((prev) =>
        prev.map((v) =>
          v.id === variantId ? { ...v, images: updatedImages } : v,
        ),
      );
    } catch (err) {
      console.error("Error uploading image:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al subir la imagen",
      );
    } finally {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: false }));
    }
  };

  const handleDeleteImage = async (variantId: string, imageId: number) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");
      const idCompany = (profile as any).id_company as string;

      // Get image record to know filename
      const { data: imageData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("id", imageId)
        .eq("id_company", idCompany)
        .single();

      const filename = imageData?.url || "";

      if (filename && !filename.startsWith("http")) {
        const fullPath = `${idCompany}/${variantId}/full/${filename}`;
        const optPath = `${idCompany}/${variantId}/opt/${filename}`;
        await supabase.storage.from("productos").remove([fullPath, optPath]);
      }

      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("id", imageId)
        .eq("id_company", idCompany);

      // Ensure one principal remains
      let { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url, principal, orden, variante_id")
        .eq("variante_id", variantId)
        .eq("id_company", idCompany)
        .order("orden", { ascending: true });

      if (
        (imagesData || []).length > 0 &&
        !(imagesData || []).some((img: any) => img.principal)
      ) {
        const firstId = (imagesData as any)[0].id;
        await supabase
          .from("productos_imagenes")
          .update({ principal: true })
          .eq("id", firstId)
          .eq("id_company", idCompany);

        // Re-fetch after setting principal
        const re = await supabase
          .from("productos_imagenes")
          .select("id, url, principal, orden, variante_id")
          .eq("variante_id", variantId)
          .eq("id_company", idCompany)
          .order("orden", { ascending: true });
        imagesData = re.data as any;
      }

      const updatedImages: ProductImage[] = (imagesData || []).map(
        (img: any) => ({
          id: img.id,
          url: img.url,
          principal: img.principal,
          orden: img.orden,
          producto_variante_id: 0,
          variante_id: img.variante_id,
        }),
      );

      setEditingVariant((prev) =>
        prev && prev.id === variantId
          ? { ...prev, images: updatedImages }
          : prev,
      );
      setVariants((prev) =>
        prev.map((v) =>
          v.id === variantId ? { ...v, images: updatedImages } : v,
        ),
      );
    } catch (err) {
      console.error("Error deleting image:", err);
      setError(
        err instanceof Error ? err.message : "Error al eliminar la imagen",
      );
    }
  };

  const handleReorderImages = async (
    _variantId: string,
    _imageId: number,
    _direction: "up" | "down",
  ) => {
    // Placeholder for future implementation
    return;
  };

  // Cascade de borrado de una variante (storage full+opt → imágenes → variante →
  // producto si queda sin variantes). Sin efectos de UI — reutilizable por el
  // borrado single y por el borrado en cantidad.
  const deleteVariantCascade = useCallback(
    async (variantId: string, companyId: string): Promise<void> => {
      // 1) variante → product_id
      const { data: variantRow, error: variantSelErr } = await supabase
        .from("variantes")
        .select("id, product_id")
        .eq("id", variantId)
        .eq("id_company", companyId)
        .single();
      if (variantSelErr) throw variantSelErr;
      const productId = (variantRow as any)?.product_id as string | null;

      // 2) imágenes → borrar de storage
      const { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("variante_id", variantId)
        .eq("id_company", companyId);
      const filesToRemove: string[] = [];
      (imagesData || []).forEach((img: any) => {
        const filename = (img?.url as string) || "";
        if (filename && !filename.startsWith("http")) {
          filesToRemove.push(`${companyId}/${variantId}/full/${filename}`);
          filesToRemove.push(`${companyId}/${variantId}/opt/${filename}`);
        }
      });
      if (filesToRemove.length > 0) {
        try {
          await supabase.storage.from("productos").remove(filesToRemove);
        } catch {}
      }

      // 3) borrar filas de imágenes
      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("variante_id", variantId)
        .eq("id_company", companyId);

      // 4) borrar variante
      await supabase
        .from("variantes")
        .delete()
        .eq("id", variantId)
        .eq("id_company", companyId);

      // 5) si el producto no tiene más variantes → borrar producto
      if (productId) {
        const { data: otherVariants } = await supabase
          .from("variantes")
          .select("id")
          .eq("product_id", productId)
          .eq("id_company", companyId)
          .limit(1);
        if (!otherVariants || otherVariants.length === 0) {
          try {
            await supabase
              .from("productos")
              .delete()
              .eq("id", productId)
              .eq("id_company", companyId);
          } catch {}
        }
      }
    },
    [],
  );

  // Delete an entire product variant (and clean up images + product if empty)
  const handleDeleteVariant = async (variantId: string) => {
    try {
      setSavingProduct(true);
      setImageError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      if (!(profile as any)?.id_company)
        throw new Error("No company associated with user");
      const resolvedCompanyId = (profile as any).id_company as string;

      await deleteVariantCascade(variantId, resolvedCompanyId);

      // Update local lists and selections
      setEditingVariant(null);
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
      setSelectedVariants((prev) => {
        const next = prev.filter((id) => id !== variantId);
        mergeLastCrearAnuncioState({ selectedVariantIds: next });
        return next;
      });
    } catch (err) {
      console.error("Error deleting variant:", err);
      setError(
        err instanceof Error ? err.message : "Error al eliminar el producto",
      );
    } finally {
      setSavingProduct(false);
    }
  };

  const principalImageUrl = (variant: Variant): string | null => {
    const img =
      (variant.images || []).find((i) => i.principal) ||
      (variant.images || [])[0];
    if (!img?.url) return null;
    const urlKey = img.url;
    if (urlKey.startsWith("http")) return urlKey;
    const signed = signedUrls[urlKey];
    return signed?.thumbnail || null;
  };

  // Sign principal image URL for a variant on demand
  const ensurePrincipalImageSigned = async (variantId: string) => {
    const variant = variants.find((v) => v.id === variantId);
    if (!variant) return;
    const img =
      (variant.images || []).find((i) => i.principal) ||
      (variant.images || [])[0];
    if (!img?.url) return;
    const key = img.url as string;

    if (key.startsWith("http")) {
      if (!signedUrls[key]) {
        setSignedUrls((prev) => ({
          ...prev,
          [key]: { full: key, thumbnail: key },
        }));
      }

      return;
    }
    const cid = companyIdRef.current;
    if (!cid) return;
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;

    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? variant.id;
      const optFilePath = `${cid}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${cid}/${pathVariantId}/full/${key}`;

      let thumbSigned: string | null = getCachedSignedUrl(optFilePath);
      let fullSigned: string | null = getCachedSignedUrl(fullFilePath);

      if (!thumbSigned || !fullSigned) {
        // Request both signed URLs in parallel
        const [thumbRes, fullRes] = await Promise.all([
          supabase.storage.from("productos").createSignedUrl(optFilePath, 3600),
          supabase.storage
            .from("productos")
            .createSignedUrl(fullFilePath, 3600),
        ]);

        if (thumbRes.data?.signedUrl) {
          thumbSigned = thumbRes.data.signedUrl;
          setCachedSignedUrl(optFilePath, thumbSigned);
        }
        if (fullRes.data?.signedUrl) {
          fullSigned = fullRes.data.signedUrl;
          setCachedSignedUrl(fullFilePath, fullSigned);
        }
        // Fallback: use whichever is available
        if (!thumbSigned && fullSigned) thumbSigned = fullSigned;
        if (!fullSigned && thumbSigned) fullSigned = thumbSigned;
      }

      // Fetch cached image blobs in parallel
      let thumbOut: string | null = null;
      let fullOut: string | null = null;
      const thumbKeyStable = `productos/${optFilePath}`;
      const fullKeyStable = `productos/${fullFilePath}`;

      const [thumbCached, fullCached] = await Promise.all([
        thumbSigned
          ? getCachedImageByKey(thumbKeyStable, thumbSigned)
          : Promise.resolve(null),
        fullSigned
          ? getCachedImageByKey(fullKeyStable, fullSigned)
          : Promise.resolve(null),
      ]);
      if (thumbCached) {
        thumbOut = thumbCached.url;
        cachedKeysRef.current.add(thumbKeyStable);
      }
      if (fullCached) {
        fullOut = fullCached.url;
        cachedKeysRef.current.add(fullKeyStable);
      }

      setSignedUrls((prev) => ({
        ...prev,
        [key]: { full: fullOut || key, thumbnail: thumbOut || fullOut || key },
      }));
    } catch (e) {
      console.error("[SPA] ensureSign:error", e);
      setSignedUrls((prev) => ({
        ...prev,
        [key]: {
          full: "https://via.placeholder.com/300x300?text=Error",
          thumbnail: "https://via.placeholder.com/300x300?text=Error",
        },
      }));
    } finally {
      loadingImageKeysRef.current.delete(key);
    }
  };

  // Filtered variants by query
  const filteredVariants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter((v) => (v.nombre || "").toLowerCase().includes(q));
  }, [variants, query]);

  // Observe cards and load images on demand
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const idAttr = el.getAttribute("data-variant-id");
            const vid = idAttr || "";
            if (vid) {
              ensurePrincipalImageSigned(vid as string);
            }
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 },
    );

    // Start observing all visible cards
    filteredVariants.forEach((v) => {
      const el = cardRefs.current[String(v.id)];
      if (el) {
        observerRef.current!.observe(el);
      }
    });

    return () => observerRef.current?.disconnect();
  }, [filteredVariants, variants]); // companyId accessed via ref, no need to re-create observer on companyId change


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("modo")) {
      mergeLastCrearAnuncioState({ modo });
    }
  }, [modo, location.search]);

  const greeting = getGreeting();
  const greetingText = shortName
    ? `${greeting}, ${shortName}.`
    : `${greeting}.`;

  return (
    <>
      <div
        className="fixed inset-0 bg-surface-mobile sm:bg-transparent pointer-events-none sm:hidden"
        aria-hidden="true"
      />
      <div className="relative p-4 pb-28 sm:p-8 sm:pb-0 max-w-content mx-auto bg-surface-mobile sm:bg-transparent min-h-screen">
        {/* Mobile header with greeting */}
        <div className="sm:hidden mt-6 mb-4">
          <h1 className="text-[22px] font-normal text-brand-black/90 text-center mt-2 mb-10 font-display italic">
            {greetingText}
          </h1>

          {/* Banners de home (prueba gratis / marca / cuestionario / Tienda
              Nube) — compartido con V2. */}
          <HomeBannersView state={homeBanners} showStatus />

          <h2 className="text-[15px] font-medium text-brand-black/90 mt-6">
            Selecciona productos
          </h2>
          <p className="text-[15px] text-[#3C3C43]/60 mt-1">
            Los anuncios generados se crearán utilizando los productos
            seleccionados.
          </p>
        </div>

        {/* Desktop header with greeting */}
        <div className="hidden sm:block mb-6">
          <h1 className="text-[28px] font-normal text-brand-black/90 text-center font-display italic">
            {greetingText}
          </h1>
          {randomMessage && (
            <p className="text-[15px] text-[#3C3C43]/60 text-center mt-2 mb-6">
              {randomMessage}
            </p>
          )}

          {/* Banners de home (prueba gratis / marca / cuestionario / Tienda
              Nube) — compartido con V2. */}
          <HomeBannersView state={homeBanners} />

          <div className="mt-4">
            <h2 className="text-[17px] font-semibold text-brand-black/90">
              Selecciona productos
            </h2>
            <p className="text-[#3C3C43]/60 text-[13px] sm:text-[15px] mt-1">
              Elegí hasta 3 productos. Los anuncios generados se crearán
              utilizando los productos seleccionados.
            </p>
          </div>
        </div>

        {/* Search bar and Add product button */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <label htmlFor="search-productos" className="sr-only">
                Buscar productos
              </label>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                id="search-productos"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar productos..."
                className="w-full pl-10 pr-3 h-[var(--btn-h)] rounded-md border border-neutral-200 bg-surface-card text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
              />
            </div>

            {/* Brand switcher (solo si el user tiene >1 marca) */}
            {brands.length > 1 && activeBrand && (
              <div
                ref={brandSwitcherRef}
                className="relative w-full sm:w-auto"
              >
                <button
                  type="button"
                  onClick={() => setIsBrandDropdownOpen((v) => !v)}
                  className="w-full sm:w-auto inline-flex items-center justify-between sm:justify-start gap-2 h-[var(--btn-h)] pl-1.5 pr-3 border border-neutral-200 hover:border-neutral-300 bg-white rounded-md text-[length:var(--text-body)] font-medium text-brand-black/90 transition-colors disabled:opacity-60"
                  aria-haspopup="listbox"
                  aria-expanded={isBrandDropdownOpen}
                  disabled={switchingBrand}
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <img
                      src={
                        activeBrand.logo_url
                          ? (activeBrand.logo_url.includes("supabase.co")
                              ? `${activeBrand.logo_url}?width=32&height=32`
                              : activeBrand.logo_url)
                          : "/images/estudio-atlas-logo-solo.svg"
                      }
                      alt=""
                      className="w-7 h-7 rounded-[6px] object-contain bg-white border border-neutral-200 shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/estudio-atlas-logo-solo.svg";
                      }}
                    />
                    <span className="truncate max-w-[160px]">
                      {activeBrand.name}
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-neutral-400 shrink-0 transition-transform ${
                      isBrandDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isBrandDropdownOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 sm:right-0 left-0 sm:left-auto z-30 mt-1 w-full sm:w-64 bg-white border border-neutral-200 rounded-md shadow-lg max-h-[60vh] overflow-y-auto scrollbar-thin"
                  >
                    {brands.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        role="option"
                        aria-selected={b.id === currentBrandId}
                        onClick={() => switchBrand(b.id)}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors"
                      >
                        <img
                          src={
                            b.logo_url
                              ? (b.logo_url.includes("supabase.co")
                                  ? `${b.logo_url}?width=32&height=32`
                                  : b.logo_url)
                              : "/images/estudio-atlas-logo-solo.svg"
                          }
                          alt=""
                          className="w-7 h-7 rounded-[6px] object-contain bg-white border border-neutral-200 shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/estudio-atlas-logo-solo.svg";
                          }}
                        />
                        <span className="flex-1 text-[length:var(--text-body)] font-medium text-brand-black/90 truncate">
                          {b.name}
                        </span>
                        {b.id === currentBrandId && (
                          <Check className="w-4 h-4 text-brand-primary shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar producto
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center text-error-text bg-error-soft border border-error/20 rounded-md p-3">
            <AlertCircle className="w-4 h-4 mr-2 shrink-0" />
            <span className="text-[length:var(--text-body)]">{error}</span>
            {/* Sin esto, un fallo de red sólo se sale recargando la página a mano. */}
            {esErrorReintentable && (
              <button
                type="button"
                onClick={() => fetchVariants()}
                disabled={loading}
                className="ml-auto shrink-0 rounded-md border border-error/30 py-2 px-4 text-[length:var(--text-body)] font-medium hover:bg-error/10 disabled:opacity-50"
              >
                Reintentar
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="border border-neutral-200 rounded-lg overflow-hidden"
              >
                <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
                <div className="p-3">
                  <div className="h-4 bg-neutral-100 rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {variants.length === 0 ? (
              <div className="bg-surface-card border border-neutral-200 rounded-lg p-6 sm:p-8 text-center">
                <p className="text-[length:var(--text-body)] text-neutral-600">
                  No tenés productos cargados todavía.
                </p>
                <div className="mt-4 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center justify-center h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Agregar producto
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                {filteredVariants.map((variant) => {
                  const imgUrl = principalImageUrl(variant);
                  const selected = selectedVariants.includes(variant.id);
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => handleVariantSelect(variant.id)}
                      className={`group relative text-left sm:bg-white rounded-lg overflow-hidden transition-all border-2 ${selected ? "border-brand-primary" : "border-neutral-200 hover:border-neutral-300"}`}
                      ref={(el) => {
                        cardRefs.current[variant.id] = el;
                      }}
                      data-variant-id={variant.id}
                    >
                      <div className="relative">
                        {imgUrl ? (
                          <CachedImg
                            src={imgUrl}
                            alt={variant.nombre}
                            className="w-full aspect-square object-cover bg-neutral-100"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-neutral-400" />
                          </div>
                        )}
                        <span
                          className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${selected ? "bg-brand-primary text-white shadow-sm" : "border-2 border-neutral-300 bg-white/70"}`}
                        >
                          {selected && <Check className="w-3.5 h-3.5" />}
                        </span>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[length:var(--text-body)] text-neutral-900 truncate font-medium">
                            {variant.nombre}
                          </p>
                          <span
                            role="button"
                            tabIndex={0}
                            title="Editar"
                            aria-label="Editar"
                            className={`p-1.5 rounded-md transition-colors shrink-0 ${selected ? "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100" : "text-transparent pointer-events-none"}`}
                            onClick={(e) => handleEditIconClick(e, variant.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleEditIconClick(e as any, variant.id);
                              }
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Spacer for fixed desktop bar (to avoid overlap) */}
        {selectedVariants.length > 0 && (
          <div className="hidden sm:block h-16" />
        )}

        {/* Fixed bottom bar (desktop only) */}
        {selectedVariants.length > 0 && (
          <div
            className="hidden sm:block fixed bottom-0 right-0 bg-surface-card border-t border-neutral-200 p-3 z-20 transition-[left] duration-300 ease-out"
            style={{ left: sidebarWidth }}
          >
            <div className="max-w-content mx-auto relative flex items-center justify-between gap-4">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-5 h-[var(--btn-h)] bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
                onClick={() => {
                  setSelectedVariants([]);
                  mergeLastCrearAnuncioState({ selectedVariantIds: [] });
                  // Limpiar también el param ?ids= de la URL para que no resucite la selección al refrescar
                  {
                    const qp = new URLSearchParams(location.search);
                    qp.delete("ids");
                    const qs = qp.toString();
                    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
                  }
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setShowBulkDeleteConfirm(true)}
                disabled={bulkDeleting}
                className="inline-flex items-center justify-center w-[var(--btn-h)] h-[var(--btn-h)] bg-surface-card hover:bg-error-soft text-neutral-500 hover:text-error border border-neutral-200 rounded-md shadow-sm transition-colors disabled:opacity-50"
                aria-label="Eliminar seleccionados"
                title="Eliminar seleccionados"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 text-[length:var(--text-body)] text-neutral-600 pointer-events-none">
                {selectedVariants.length}{" "}
                {selectedVariants.length === 1
                  ? "producto seleccionado"
                  : "productos seleccionados"}
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-5 h-[var(--btn-h)] bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
                  onClick={handleVideoGenerationClick}
                >
                  <Video className="w-4 h-4" />
                  Generar video
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-5 h-[var(--btn-h)] bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium whitespace-nowrap transition-colors"
                  onClick={handleImageGenerationClick}
                >
                  <ImageIcon className="w-4 h-4" />
                  Generar imagen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile spacer for fixed bottom bar */}
        {selectedVariants.length > 0 && (
          <div
            className="sm:hidden"
            style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 68px)" }}
          />
        )}

        {selectedVariants.length === 0 && isFreePlan && (
          <>
            {/* Mobile: direct WhatsApp link */}
            <a
              href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tenia una duda.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden fixed left-3 z-30 inline-flex items-center gap-2 px-4 h-10 rounded-full border border-neutral-200 bg-white text-brand-black/90 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary text-[15px]"
              style={{
                // Por encima de la barra de navegación inferior (h-16 + safe-area).
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 4rem + 12px)",
              }}
              aria-label="Contactanos por WhatsApp"
              title="Contactanos por WhatsApp"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-brand-black/60"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.0781 4.9151C17.1693 2.99084 14.6008 1.93602 11.868 1.93945C6.25977 1.93945 1.70409 6.49408 1.7002 12.1084C1.69925 13.8729 2.15811 15.6037 2.99707 17.1318L1.60547 22.4004L7.00879 21.0381C8.48438 21.8008 10.1602 22.2 11.8633 22.2002H11.8691C17.4756 22.2002 22.0322 17.6445 22.0361 12.0293C22.0381 9.30713 20.9863 6.83936 19.0781 4.9151ZM11.8682 20.5254H11.8633C10.3457 20.5254 8.85645 20.1484 7.55371 19.4424L7.24609 19.2686L4.12305 20.1025L4.97266 17.0557L4.7793 16.7334C3.99023 15.375 3.57031 13.7646 3.57129 12.1094C3.57422 7.52832 7.29004 3.81543 11.873 3.81543C14.1475 3.81055 16.2803 4.6875 17.877 6.29639C19.4736 7.90527 20.3447 10.0459 20.3438 12.3281C20.3398 16.9102 16.624 20.5254 11.8682 20.5254ZM16.5898 14.2168C16.3389 14.0908 15.1201 13.4922 14.8896 13.4077C14.6592 13.3232 14.4922 13.2812 14.3242 13.5333C14.1562 13.7854 13.6816 14.3413 13.5322 14.5088C13.3828 14.6762 13.2344 14.6967 12.9834 14.5708C12.7324 14.4448 11.9302 14.1885 10.9932 13.3525C10.2666 12.7041 9.77832 11.9102 9.62891 11.6582C9.47949 11.4062 9.61426 11.2671 9.7432 11.1392C9.85742 11.0244 9.99609 10.8384 10.125 10.689C10.2539 10.5396 10.2969 10.4346 10.3809 10.2666C10.4648 10.0986 10.4219 9.94921 10.3594 9.82323C10.2969 9.69726 9.81152 8.47364 9.60254 7.96973C9.39844 7.47949 9.19141 7.54196 9.03418 7.53417C8.88477 7.52734 8.71777 7.52539 8.54980 7.52539C8.38184 7.52539 8.10742 7.58789 7.87695 7.83986C7.64648 8.09183 7.00586 8.69043 7.00586 9.91308C7.00586 11.1357 7.89844 12.3164 8.02734 12.4844C8.15625 12.6523 9.77637 15.1104 12.2227 16.1816C12.8281 16.4414 13.2998 16.5977 13.667 16.7158C14.2852 16.9102 14.8477 16.8828 15.292 16.8193C15.791 16.749 16.7783 16.2207 16.9873 15.6318C17.1963 15.043 17.1963 14.5391 17.1338 14.5088C17.0713 14.4785 16.8408 14.3428 16.5898 14.2168Z"
                />
              </svg>
              Contactanos
            </a>
            {/* Desktop: button that shows QR modal */}
            <button
              type="button"
              onClick={() => setShowContactQR(true)}
              className="hidden sm:inline-flex fixed right-6 top-[22px] z-30 items-center gap-2 px-4 h-10 rounded-full border border-neutral-200 bg-white text-brand-black/90 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary text-[15px]"
              aria-label="Contactanos por WhatsApp"
              title="Contactanos por WhatsApp"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-brand-black/60"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.0781 4.9151C17.1693 2.99084 14.6008 1.93602 11.868 1.93945C6.25977 1.93945 1.70409 6.49408 1.7002 12.1084C1.69925 13.8729 2.15811 15.6037 2.99707 17.1318L1.60547 22.4004L7.00879 21.0381C8.48438 21.8008 10.1602 22.2 11.8633 22.2002H11.8691C17.4756 22.2002 22.0322 17.6445 22.0361 12.0293C22.0381 9.30713 20.9863 6.83936 19.0781 4.9151ZM11.8682 20.5254H11.8633C10.3457 20.5254 8.85645 20.1484 7.55371 19.4424L7.24609 19.2686L4.12305 20.1025L4.97266 17.0557L4.7793 16.7334C3.99023 15.375 3.57031 13.7646 3.57129 12.1094C3.57422 7.52832 7.29004 3.81543 11.873 3.81543C14.1475 3.81055 16.2803 4.6875 17.877 6.29639C19.4736 7.90527 20.3447 10.0459 20.3438 12.3281C20.3398 16.9102 16.624 20.5254 11.8682 20.5254ZM16.5898 14.2168C16.3389 14.0908 15.1201 13.4922 14.8896 13.4077C14.6592 13.3232 14.4922 13.2812 14.3242 13.5333C14.1562 13.7854 13.6816 14.3413 13.5322 14.5088C13.3828 14.6762 13.2344 14.6967 12.9834 14.5708C12.7324 14.4448 11.9302 14.1885 10.9932 13.3525C10.2666 12.7041 9.77832 11.9102 9.62891 11.6582C9.47949 11.4062 9.61426 11.2671 9.7432 11.1392C9.85742 11.0244 9.99609 10.8384 10.125 10.689C10.2539 10.5396 10.2969 10.4346 10.3809 10.2666C10.4648 10.0986 10.4219 9.94921 10.3594 9.82323C10.2969 9.69726 9.81152 8.47364 9.60254 7.96973C9.39844 7.47949 9.19141 7.54196 9.03418 7.53417C8.88477 7.52734 8.71777 7.52539 8.54980 7.52539C8.38184 7.52539 8.10742 7.58789 7.87695 7.83986C7.64648 8.09183 7.00586 8.69043 7.00586 9.91308C7.00586 11.1357 7.89844 12.3164 8.02734 12.4844C8.15625 12.6523 9.77637 15.1104 12.2227 16.1816C12.8281 16.4414 13.2998 16.5977 13.667 16.7158C14.2852 16.9102 14.8477 16.8828 15.292 16.8193C15.791 16.749 16.7783 16.2207 16.9873 15.6318C17.1963 15.043 17.1963 14.5391 17.1338 14.5088C17.0713 14.4785 16.8408 14.3428 16.5898 14.2168Z"
                />
              </svg>
              Contactanos
            </button>
          </>
        )}

        {/* Mobile bottom bar with Continue + Repeat */}
        {selectedVariants.length > 0 && (
          <div
            className="sm:hidden fixed bottom-0 left-0 right-0 bg-surface-card border-t border-neutral-200 p-3 z-30"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            }}
          >
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated"
                onClick={() => {
                  setSelectedVariants([]);
                  mergeLastCrearAnuncioState({ selectedVariantIds: [] });
                  // Limpiar también el param ?ids= de la URL para que no resucite la selección al refrescar
                  {
                    const qp = new URLSearchParams(location.search);
                    qp.delete("ids");
                    const qs = qp.toString();
                    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
                  }
                }}
              >
                <X className="w-5 h-5" />
                <span>Cancelar</span>
              </button>
              <button
                type="button"
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm"
                onClick={handleVideoGenerationClick}
              >
                <Video className="w-5 h-5" />
                <span>Generar video</span>
              </button>
              <button
                type="button"
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm"
                onClick={handleImageGenerationClick}
              >
                <ImageIcon className="w-5 h-5" />
                <span>Generar imagen</span>
              </button>
            </div>
          </div>
        )}


        {/* El modal del cuestionario ahora lo monta `HomeBannersView` junto a su
            banner (mismo patrón que el modal de Tienda Nube), así lo tienen las
            dos interfaces sin duplicarlo por página. */}

        {/* Video plan restriction modal */}
        {showVideoPlanRestriction && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
            <div className="bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md relative flex flex-col sm:flex-row sm:overflow-hidden">
              {/* Close button */}
              <button
                type="button"
                onClick={() => setShowVideoPlanRestriction(false)}
                className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 sm:text-neutral-400 sm:hover:text-neutral-600 sm:hover:bg-neutral-100 inline-flex items-center justify-center transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Title — mobile only (above video) */}
              <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">
                Función exclusiva
              </h3>

              {/* Video */}
              <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
                <video
                  src="/images/video_animar_imagen.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg"
                />
                <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden">
                  <Lock className="w-5 h-5 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
                {/* Title — desktop only */}
                <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">
                  Función exclusiva
                </h3>
                <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">
                  La generación de videos está disponible únicamente para los
                  usuarios de los planes{" "}
                  <span className="font-semibold text-brand-black/80">Pro</span>{" "}
                  y{" "}
                  <span className="font-semibold text-brand-black/80">
                    Ultra
                  </span>
                  .
                </p>
                <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
                  {!enPortalStripe ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowVideoPlanRestriction(false);
                        navigate("/subscription");
                      }}
                      className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                    >
                      {userPlan === "gratis" ? "Ver planes" : "Mejorar plan"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleOpenBillingPortal}
                      disabled={openingBillingPortal}
                      className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                    >
                      {openingBillingPortal ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Mejorar plan"
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowVideoPlanRestriction(false)}
                    className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk delete confirmation modal (desktop) */}
        {showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[70]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Eliminar productos
                </h3>
                <button
                  onClick={() =>
                    !bulkDeleting && setShowBulkDeleteConfirm(false)
                  }
                  disabled={bulkDeleting}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-error-soft flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-5 h-5 text-error" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  {bulkDeleting
                    ? `Eliminando ${bulkDeleteProgress.done}/${bulkDeleteProgress.total}...`
                    : `¿Seguro que querés eliminar ${selectedVariants.length} ${
                        selectedVariants.length === 1 ? "producto" : "productos"
                      }? Esta acción no se puede deshacer.`}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleting}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className={`h-[var(--btn-h)] px-5 inline-flex items-center justify-center rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-error hover:bg-red-600 text-white ${
                    bulkDeleting ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                >
                  {bulkDeleting ? "Eliminando..." : "Eliminar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video multiple products warning modal */}
        {showVideoMultipleProductsWarning && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
              <button
                type="button"
                onClick={() => setShowVideoMultipleProductsWarning(false)}
                className="absolute top-3 right-3 p-2 text-brand-black/40 hover:text-brand-black/60 rounded-md focus:outline-none"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                Un producto a la vez
              </h3>
              <p className="text-[15px] text-[#3C3C43]/60 mb-5">
                Por el momento, la generación de video solo admite generar de a
                1 producto a la vez.
              </p>
              <button
                type="button"
                onClick={() => setShowVideoMultipleProductsWarning(false)}
                className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full text-[15px] font-medium"
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {/* Max 3 products for image generation warning modal */}
        {showMaxImageProductsWarning && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
              <button
                type="button"
                onClick={() => setShowMaxImageProductsWarning(false)}
                className="absolute top-3 right-3 p-2 text-brand-black/40 hover:text-brand-black/60 rounded-md focus:outline-none"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                Máximo 3 productos
              </h3>
              <p className="text-[15px] text-[#3C3C43]/60 mb-5">
                Para generar una imagen podés elegir hasta 3 productos.
                Deseleccioná algunos e intentá de nuevo.
              </p>
              <button
                type="button"
                onClick={() => setShowMaxImageProductsWarning(false)}
                className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full text-[15px] font-medium"
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {showAddForm && (
          <VariantForm
            editingVariant={null}
            newProduct={newProduct}
            loading={savingProduct}
            uploadingImages={{}}
            imageError={imageError}
            onCancel={() => setShowAddForm(false)}
            onSave={handleSaveProduct}
            onImageUpload={() => {}}
            onDeleteImage={() => {}}
            onSetPrincipalImage={() => {}}
            onReorderImages={() => {}}
            setEditingVariant={() => {}}
            setNewProduct={setNewProduct}
            rubro={userRubro}
            idCompany={companyId}
            userPlan={userPlan}
            onBulkSuccess={fetchVariants}
            brandId={currentBrandId}
          />
        )}

        {editingVariant && (
          <VariantForm
            editingVariant={editingVariant}
            newProduct={newProduct}
            loading={savingProduct}
            uploadingImages={uploadingImages}
            imageError={imageError}
            onCancel={() => setEditingVariant(null)}
            onSave={handleSaveProduct}
            onImageUpload={handleImageUpload}
            onDeleteImage={handleDeleteImage}
            onSetPrincipalImage={handleSetPrincipalImage}
            onReorderImages={handleReorderImages}
            setEditingVariant={setEditingVariant}
            setNewProduct={setNewProduct}
            onDeleteVariant={handleDeleteVariant}
            rubro={userRubro}
          />
        )}

        {/* Contact QR Modal - Desktop only */}
        {showContactQR && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6 relative">
              <button
                onClick={() => setShowContactQR(false)}
                className="absolute top-4 right-4 text-brand-black/30 hover:text-brand-black/60"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center">
                Contactanos por WhatsApp
              </h3>
              <p className="text-[15px] text-[#3C3C43]/60 mb-4 text-center">
                Escaneá el código QR con tu celular para abrir WhatsApp
              </p>
              <div className="flex justify-center mb-4">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tenia una duda.`)}`)}`}
                  alt="QR Code WhatsApp"
                  className="w-48 h-48"
                />
              </div>
              <button
                onClick={() => setShowContactQR(false)}
                className="w-full py-2 px-4 bg-neutral-100 text-brand-black/60 rounded-full hover:bg-neutral-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>

      <AddToHomescreenPrompt
        appName="Atlas"
        appIconUrl="/favicon.png"
        delayMs={2000}
        origen="seleccionar_productos"
      />
    </>
  );
};

export default SeleccionarProductosAnuncio;
